import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { YIELD_DISCLAIMER } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = ["Under AED 1M", "AED 1M – 3M", "AED 3M – 5M", "AED 5M – 10M", "AED 10M+"];
const PROPERTY_TYPES = [
  "Off-plan apartment",
  "Off-plan villa / townhouse",
  "Ready apartment",
  "Ready villa / townhouse",
  "Commercial / retail",
  "Not sure yet",
];
const BEDROOMS = ["Studio", "1 BR", "2 BR", "3 BR", "4 BR", "5+ BR"];
const PURPOSES = ["End-use / primary home", "Investment / rental income", "Capital growth", "Golden Visa eligibility", "Holiday home"];
const TIMELINES = ["Immediate (within 1 month)", "1 – 3 months", "3 – 6 months", "6 – 12 months", "Just exploring"];
const LOCATIONS = [
  "Downtown Dubai",
  "Dubai Marina",
  "Palm Jumeirah",
  "Business Bay",
  "Emaar Beachfront",
  "MBR City",
  "Dubai Hills",
  "JVC / JVT",
  "Academic City",
  "Open to advisor recommendation",
];

export const FullConsultationForm = ({ compact = false }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    budget: BUDGETS[1],
    location: LOCATIONS[LOCATIONS.length - 1],
    propertyType: PROPERTY_TYPES[0],
    bedrooms: BEDROOMS[2],
    purpose: PURPOSES[1],
    timeline: TIMELINES[1],
    message: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.consent) {
      toast.error("Please accept the privacy notice to continue.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        budget: form.budget,
        project_interest: `${form.propertyType} · ${form.bedrooms} · ${form.location}`,
        message:
          `Country: ${form.country || "—"}\n` +
          `Purpose: ${form.purpose}\n` +
          `Timeline: ${form.timeline}\n` +
          `Notes: ${form.message || "—"}`,
      });
      setSuccess(true);
      toast.success("Thank you — a senior advisor will contact you within one business day.");
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="glass-strong rounded-2xl p-10 text-center" data-testid="full-form-success">
        <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-6 bg-[#c5a059]/20">
          <CheckCircle2 size={36} className="text-[#c5a059]" strokeWidth={1.5} />
        </div>
        <h3 className="font-serif-display text-3xl mb-3">Thank you</h3>
        <p className="text-white/60 max-w-md mx-auto">
          Your consultation request has been received. A senior advisor will reach out within one
          business day with a personalised shortlist and next steps.
        </p>
      </div>
    );
  }

  const inputCls = "w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c5a059] transition-colors";
  const labelCls = "text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2";
  const Field = ({ label, children, span = 1 }) => (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );

  return (
    <form onSubmit={submit} className={`space-y-5 ${compact ? "" : ""}`} data-testid="full-consultation-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Full name *">
          <input required type="text" placeholder="Your full name" value={form.name} onChange={update("name")} className={inputCls} data-testid="fc-name" />
        </Field>
        <Field label="Email *">
          <input required type="email" placeholder="you@email.com" value={form.email} onChange={update("email")} className={inputCls} data-testid="fc-email" />
        </Field>

        <Field label="WhatsApp number *">
          <div className="lead-phone-wrapper">
            <PhoneInput
              country={"ae"}
              value={form.phone}
              onChange={(val) => setForm((f) => ({ ...f, phone: val ? `+${val}` : "" }))}
              enableSearch
              searchPlaceholder="Search country"
              placeholder="5X XXX XXXX"
              inputProps={{ required: true, "data-testid": "fc-phone" }}
              specialLabel=""
            />
          </div>
        </Field>
        <Field label="Country of residence">
          <input type="text" placeholder="e.g. United Kingdom" value={form.country} onChange={update("country")} className={inputCls} data-testid="fc-country" />
        </Field>

        <Field label="Budget range">
          <select value={form.budget} onChange={update("budget")} className={inputCls} data-testid="fc-budget">
            {BUDGETS.map((b) => <option key={b} value={b} className="bg-[#0a1128]">{b}</option>)}
          </select>
        </Field>
        <Field label="Preferred location">
          <select value={form.location} onChange={update("location")} className={inputCls} data-testid="fc-location">
            {LOCATIONS.map((l) => <option key={l} value={l} className="bg-[#0a1128]">{l}</option>)}
          </select>
        </Field>

        <Field label="Property type">
          <select value={form.propertyType} onChange={update("propertyType")} className={inputCls} data-testid="fc-property-type">
            {PROPERTY_TYPES.map((p) => <option key={p} value={p} className="bg-[#0a1128]">{p}</option>)}
          </select>
        </Field>
        <Field label="Number of bedrooms">
          <select value={form.bedrooms} onChange={update("bedrooms")} className={inputCls} data-testid="fc-bedrooms">
            {BEDROOMS.map((b) => <option key={b} value={b} className="bg-[#0a1128]">{b}</option>)}
          </select>
        </Field>

        <Field label="Purchase purpose">
          <select value={form.purpose} onChange={update("purpose")} className={inputCls} data-testid="fc-purpose">
            {PURPOSES.map((p) => <option key={p} value={p} className="bg-[#0a1128]">{p}</option>)}
          </select>
        </Field>
        <Field label="Purchase timeline">
          <select value={form.timeline} onChange={update("timeline")} className={inputCls} data-testid="fc-timeline">
            {TIMELINES.map((t) => <option key={t} value={t} className="bg-[#0a1128]">{t}</option>)}
          </select>
        </Field>

        <Field label="Anything else? (optional)" span={2}>
          <textarea rows={3} placeholder="Financing, specific projects, timeline notes…" value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} data-testid="fc-message" />
        </Field>
      </div>

      <label className="flex items-start gap-3 text-xs text-white/65 leading-relaxed cursor-pointer" data-testid="fc-consent-label">
        <input type="checkbox" checked={form.consent} onChange={update("consent")} required className="mt-0.5 accent-[#c5a059] w-4 h-4 shrink-0" data-testid="fc-consent" />
        <span>
          I consent to Universal Dream Properties LLC contacting me about this enquiry and I confirm
          I have read the Privacy Policy. My information will be handled confidentially and not
          shared with third parties without my permission.
        </span>
      </label>

      <button
        type="submit"
        disabled={loading}
        className="btn-premium w-full py-4 rounded-lg text-sm font-bold tracking-[0.3em] flex items-center justify-center gap-3 disabled:opacity-70"
        data-testid="fc-submit"
      >
        {loading ? (<><Loader2 className="animate-spin" size={18} /> SENDING…</>) : (<>REQUEST CONSULTATION <Send size={16} /></>)}
      </button>

      <p className="text-[10px] text-white/40 italic leading-relaxed text-center">
        {YIELD_DISCLAIMER}
      </p>
    </form>
  );
};
